/**
 * @voltxampere/math — Householder QR decomposition + least-squares
 *
 * Sprint 8b introduces QR to solve the small rectangular least-squares
 * problems that Anderson-II acceleration (Walker & Ni 2011) issues on
 * every accelerated Newton step:
 *
 *   minimize ||F·γ - f_k||₂    subject to  γ ∈ ℝ^{m-1}
 *
 * where `F` is the `(n × m-1)` matrix of successive residual
 * differences. Anderson-II's `m` window rarely exceeds 5, so the
 * dense Householder route is cheap (~O(n·m²)) and numerically
 * stable on the ill-conditioned residual differences that plague
 * limit-cycle orbits.
 *
 * Design:
 *
 *  - Input `A` is `rows × cols` row-major, `b` is a `Float64Array`
 *    of length `rows`. Rows ≥ cols is required (overdetermined +
 *    square are the only supported shapes; an underdetermined
 *    system throws `ContractViolationError`).
 *  - The implementation builds an upper-triangular `R` in place on
 *    a cloned copy of `A`, accumulates the transformed RHS into a
 *    fresh buffer of length `rows`, then back-substitutes on the
 *    top `cols × cols` block. `A` and `b` are never mutated.
 *  - Rank deficiency is detected post-hoc: if any diagonal of `R`
 *    falls below `rcond * |R[0][0]|` (default 1e-12) we throw a
 *    `SingularMatrixError` — Anderson-II's orchestrator catches
 *    that and falls back to a plain Newton step.
 *
 * Complexity: O(rows · cols²) for the decomposition, O(cols²) for
 * back-substitution. For Anderson's m=3 windows on circuits up to
 * ~50 unknowns this is well under a millisecond per call.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError, SingularMatrixError } from "@voltxampere/shared";
/**
 * Solve the least-squares problem `A · x ≈ b` via Householder QR.
 * Returns a fresh `Float64Array` of length `cols`.
 *
 * Throws:
 *  - {@link ContractViolationError} on shape / finiteness errors.
 *  - {@link SingularMatrixError} when the effective rank is below
 *    `cols` (any `|R[i][i]|` < `rcond * |R[0][0]|`).
 *
 * @param A  Matrix `rows × cols`, `rows ≥ cols`. Not mutated.
 * @param b  Vector of length `rows`. Not mutated.
 * @param rcond  Relative condition threshold for rank detection.
 *               Defaults to `1e-12`.
 */
export function qrSolve(A, b, rcond = 1e-12) {
    const { rows, cols } = A;
    if (rows < cols) {
        throw new ContractViolationError(`qrSolve: rows (${String(rows)}) must be >= cols (${String(cols)}); underdetermined systems not supported`);
    }
    if (b.length !== rows) {
        throw new ContractViolationError(`qrSolve: b length ${String(b.length)} does not match A rows ${String(rows)}`);
    }
    if (rows === 0 || cols === 0) {
        return new Float64Array(cols);
    }
    if (!Number.isFinite(rcond) || rcond < 0) {
        throw new ContractViolationError(`qrSolve: rcond must be a non-negative finite number, got ${String(rcond)}`);
    }
    // Clone A into an augmented buffer so we can overwrite it; pack
    // the data row-major to match `Matrix.data`.
    const R = new Float64Array(A.data);
    const y = new Float64Array(b); // transformed RHS
    // Householder reflection loop: zero the sub-diagonal of each
    // column `k` by reflecting about the hyperplane normal to
    // v = x - alpha·e_k.
    //
    // The reflector vector `v` is snapshotted into a stand-alone
    // buffer before we apply it to R's columns — otherwise the inner
    // column-j updates would mutate R's sub-diagonal of column `k`
    // and later j iterations would see corrupted `v[i]` entries.
    const v = new Float64Array(rows);
    for (let k = 0; k < cols; k++) {
        // Compute ||x|| where x = R[k..rows, k].
        let normSq = 0;
        for (let i = k; i < rows; i++) {
            const vi = R[i * cols + k] ?? 0;
            normSq += vi * vi;
        }
        const norm = Math.sqrt(normSq);
        if (norm === 0) {
            // Column is already zero on and below the diagonal.
            continue;
        }
        // Sign choice maximises |x[0] - alpha| — avoids catastrophic
        // cancellation when x[0] is close to ±||x||.
        const x0 = R[k * cols + k] ?? 0;
        const alpha = x0 >= 0 ? -norm : norm;
        // Build the reflector: v[k] = x0 - alpha, v[i>k] = R[i,k].
        const vk = x0 - alpha;
        v[k] = vk;
        let vNormSq = vk * vk;
        for (let i = k + 1; i < rows; i++) {
            const vi = R[i * cols + k] ?? 0;
            v[i] = vi;
            vNormSq += vi * vi;
        }
        if (vNormSq === 0)
            continue;
        const beta = 2 / vNormSq;
        // Apply H = I - beta·v·vᵀ to the remaining columns of R.
        for (let j = k; j < cols; j++) {
            let dot = 0;
            for (let i = k; i < rows; i++) {
                dot += (v[i] ?? 0) * (R[i * cols + j] ?? 0);
            }
            const scaled = beta * dot;
            for (let i = k; i < rows; i++) {
                R[i * cols + j] = (R[i * cols + j] ?? 0) - scaled * (v[i] ?? 0);
            }
        }
        // Apply H to the RHS.
        let dotY = 0;
        for (let i = k; i < rows; i++) {
            dotY += (v[i] ?? 0) * (y[i] ?? 0);
        }
        const scaledY = beta * dotY;
        for (let i = k; i < rows; i++) {
            y[i] = (y[i] ?? 0) - scaledY * (v[i] ?? 0);
        }
        // Force the sub-diagonal of column `k` to exactly zero
        // (accumulated round-off could leave ε-sized entries).
        for (let i = k + 1; i < rows; i++) {
            R[i * cols + k] = 0;
        }
    }
    // Rank check: any diagonal of R significantly smaller than the
    // first implies rank deficiency.
    const r00 = Math.abs(R[0] ?? 0);
    if (r00 === 0) {
        throw new SingularMatrixError(`qrSolve: R[0][0] is zero — matrix has zero first column`);
    }
    const threshold = rcond * r00;
    for (let i = 1; i < cols; i++) {
        if (Math.abs(R[i * cols + i] ?? 0) < threshold) {
            throw new SingularMatrixError(`qrSolve: R[${String(i)}][${String(i)}] = ${String(R[i * cols + i])} below rank threshold ${String(threshold)}`);
        }
    }
    // Back-substitution on the top cols × cols triangle of R using y.
    const x = new Float64Array(cols);
    for (let i = cols - 1; i >= 0; i--) {
        let sum = y[i] ?? 0;
        for (let j = i + 1; j < cols; j++) {
            sum -= (R[i * cols + j] ?? 0) * (x[j] ?? 0);
        }
        x[i] = sum / (R[i * cols + i] ?? 1);
    }
    return x;
}
//# sourceMappingURL=householder.js.map